import { useEffect } from 'react'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { SidebarInset, SidebarProvider, TooltipProvider } from '@rentflowdev/ui'
import { AppSidebar } from '@/components/app-sidebar'
import { AppHeader } from '@/components/app-header'
import { AccessGate } from '@/components/access-gate'
import { isAuthenticated, redirectToAuth, tryRefreshTokens } from '@/lib/auth'
import {
	closeRealtimeSocket,
	getRealtimeSocket
} from '@/lib/realtime-socket'
import { useRealtimeNotifications } from '@/lib/use-realtime-notifications'

export const Route = createFileRoute('/_app')({
	beforeLoad: async () => {
		if (!isAuthenticated()) {
			const ok = await tryRefreshTokens()
			if (!ok) await redirectToAuth()
		}
	},
	component: AppLayout,
})

/**
 * Куда студия просит вернуть пользователя после входа.
 *
 * Публикация на сайте открывает studio.rentflow.tech; если сессии там нет,
 * студия отправляет обратно сюда с `?return=<адрес>`, рассчитывая, что мы
 * войдём и вернём человека. Параметр никто не читал, поэтому нажатие
 * «Опубликовать» приводило на главную, и объект оставался неопубликованным.
 *
 * Только собственный домен: перенаправление по адресу из запроса — это
 * открытый редирект, и по такой ссылке уводят на чужую страницу входа.
 */
function ownReturnTarget(search: string): string | null {
	const raw = new URLSearchParams(search).get('return')
	if (!raw) return null
	try {
		const url = new URL(raw)
		const ok = url.protocol === 'https:'
			&& (url.hostname === 'rentflow.tech' || url.hostname.endsWith('.rentflow.tech'))
		return ok ? url.toString() : null
	} catch {
		return null
	}
}

/** Чтобы не зациклиться, если студия отправит обратно ещё раз. */
const RETURN_ONCE_KEY = 'app:return-bounce'

function AppLayout() {
	useEffect(() => {
		// Во фрейме этот параметр значит не «верни пользователя после входа», а
		// «студия не увидела сессию» — и увести туда фрейм значит гонять его по
		// кругу. Конструктор показывает свою ошибку сам.
		if (window.top !== window.self) return
		const target = ownReturnTarget(window.location.search)
		if (!target) return
		if (sessionStorage.getItem(RETURN_ONCE_KEY) === target) {
			sessionStorage.removeItem(RETURN_ONCE_KEY)
			return
		}
		sessionStorage.setItem(RETURN_ONCE_KEY, target)
		window.location.replace(target)
	}, [])

	useEffect(() => {
		// Поднимаем единый realtime-сокет на всё приложение, гасим при размонтировании.
		getRealtimeSocket()
		return () => closeRealtimeSocket()
	}, [])

	useRealtimeNotifications()

	return (
		<TooltipProvider>
			<SidebarProvider>
				<AppSidebar />
				<SidebarInset>
					<AppHeader />
					<main className="flex-1 overflow-auto">
						<AccessGate>
							<Outlet />
						</AccessGate>
					</main>
				</SidebarInset>
			</SidebarProvider>
		</TooltipProvider>
	)
}
